import { useState } from 'react'
import { X, TestTube, CheckCircle, AlertTriangle, XCircle, ShieldCheck, ShieldX } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Props {
  isOpen: boolean
  onClose: () => void
  returnData: {
    id: number
    code: string
    customer_name: string
    product_amount: number
    refund_amount: number
    case_type?: string
  }
  onTested: () => void
}

const RETURN_TEST_RESULTS = [
  {
    value: 'ok',
    label: 'OK - Producto en buen estado',
    description: 'El producto funciona correctamente y puede reembolsarse',
    color: 'bg-green-100 border-green-300 text-green-800',
    icon: CheckCircle
  },
  {
    value: 'defective',
    label: 'Defectuoso por uso',
    description: 'El producto presenta defectos causados por mal uso del cliente',
    color: 'bg-orange-100 border-orange-300 text-orange-800',
    icon: AlertTriangle
  },
  {
    value: 'damaged',
    label: 'Dañado en transporte',
    description: 'El producto llegó dañado durante el envío de devolución',
    color: 'bg-amber-100 border-amber-300 text-amber-800',
    icon: AlertTriangle
  },
  {
    value: 'fake',
    label: 'No corresponde',
    description: 'El producto recibido no es el original enviado',
    color: 'bg-red-100 border-red-300 text-red-800',
    icon: XCircle
  }
]

const WARRANTY_TEST_RESULTS = [
  {
    value: 'warranty_confirmed',
    label: 'Defecto confirmado',
    description: 'El fallo descrito por el cliente se ha verificado. La garantía procede.',
    color: 'bg-green-100 border-green-300 text-green-800',
    icon: ShieldCheck
  },
  {
    value: 'warranty_wear',
    label: 'Desgaste / mal uso',
    description: 'El daño es por uso o desgaste, no por defecto de fábrica. Garantía no procede.',
    color: 'bg-orange-100 border-orange-300 text-orange-800',
    icon: ShieldX
  },
  {
    value: 'warranty_not_reproducible',
    label: 'Fallo no reproducible',
    description: 'No se ha podido replicar el defecto descrito por el cliente.',
    color: 'bg-amber-100 border-amber-300 text-amber-800',
    icon: AlertTriangle
  },
  {
    value: 'warranty_no_defect',
    label: 'Sin defecto',
    description: 'El producto funciona correctamente. No se detecta ningún fallo.',
    color: 'bg-red-100 border-red-300 text-red-800',
    icon: XCircle
  }
]

export default function TestReturnModal({ isOpen, onClose, returnData, onTested }: Props) {
  const [testResult, setTestResult] = useState('')
  const [testNotes, setTestNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const isWarranty = returnData.case_type === 'warranty'
  const testResults = isWarranty ? WARRANTY_TEST_RESULTS : RETURN_TEST_RESULTS

  const handleTest = async () => {
    if (!testResult) {
      toast.error('Error', 'Debes seleccionar un resultado de testeo')
      return
    }

    setLoading(true)
    try {
      await api.post(`/returns/admin/${returnData.id}/tested`, {
        test_result: testResult,
        test_notes: testNotes || undefined
      })

      toast.success('Exito', `Testeo registrado para ${returnData.code}`)

      onTested()
      onClose()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al registrar testeo')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  // Resultado positivo: ok (devolución) o warranty_confirmed (garantía)
  const isPositiveResult = testResult === 'ok' || testResult === 'warranty_confirmed'
  const isNegativeResult = testResult && !isPositiveResult

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isWarranty ? 'bg-amber-100' : 'bg-purple-100'}`}>
              <TestTube className={`w-5 h-5 ${isWarranty ? 'text-amber-600' : 'text-purple-600'}`} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {isWarranty ? 'Evaluación de Garantía' : 'Registrar Testeo'}
              </h2>
              <p className="text-sm text-slate-500">{returnData.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Selector de resultado */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              {isWarranty ? 'Resultado de la evaluación' : 'Resultado del testeo'}
            </label>
            <div className="space-y-2">
              {testResults.map(result => {
                const Icon = result.icon
                const isSelected = testResult === result.value

                return (
                  <button
                    key={result.value}
                    onClick={() => setTestResult(result.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? result.color + ' border-current'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? '' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${isSelected ? '' : 'text-slate-900 dark:text-white'}`}>
                          {result.label}
                        </p>
                        <p className={`text-sm mt-0.5 ${isSelected ? 'opacity-80' : 'text-slate-500'}`}>
                          {result.description}
                        </p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Notas del testeo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {isWarranty ? 'Notas de la evaluación' : 'Notas del testeo'}
            </label>
            <textarea
              value={testNotes}
              onChange={(e) => setTestNotes(e.target.value)}
              placeholder={isWarranty
                ? 'Describe las pruebas realizadas, resultado de la evaluación...'
                : 'Describe el estado del producto, pruebas realizadas...'
              }
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white resize-none"
            />
          </div>

          {/* Info siguiente paso */}
          {isPositiveResult && !isWarranty && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-300">
                Tras registrar el testeo como OK, podras procesar el reembolso por valor de <strong>{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(returnData.refund_amount)}</strong>.
              </p>
            </div>
          )}

          {isPositiveResult && isWarranty && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-300">
                El defecto ha sido confirmado. Se podrá proceder con el envío de sustitución o reembolso al cliente.
              </p>
            </div>
          )}

          {isNegativeResult && !isWarranty && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Si el producto no esta en condiciones, podras decidir si proceder con un reembolso parcial o contactar con el cliente.
              </p>
            </div>
          )}

          {isNegativeResult && isWarranty && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                La garantía no procede. Se informará al cliente del resultado y de las opciones disponibles (devolución del producto o envío a portes debidos).
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleTest}
            disabled={loading || !testResult}
            className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center gap-2 ${
              isWarranty ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'
            }`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              <>
                <TestTube className="w-4 h-4" />
                {isWarranty ? 'Registrar evaluación' : 'Registrar testeo'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
